import React, {useMemo, useState} from "react";
import {BarChart3, FileText, LayoutDashboard, Shield, ShieldCheck} from "lucide-react";
import {useLocation, useNavigate} from "react-router-dom";
import {Box, ButtonBase, Typography, useTheme} from "@mui/material";
import {PageHeader} from "../../shared/ui";
import LicencasDashboardPage from "./LicencasDashboardPage";
import LicencasGestaoPage from "./LicencasGestaoPage";
import PermissionsPage from "../Permissions/PermissionsPage";

const tabs = [
    {id: 'dashboard', label: 'Dashboard', icon: BarChart3, path: '/licencas/dashboard'},
    {id: 'licencas', label: 'Licenças', icon: FileText, path: '/licencas/gestao'},
    {id: 'politicas-acesso', label: 'Políticas de Acesso', icon: Shield, path: '/licencas/permissoes'},
];

const getActiveTab = (pathname) => {
    if (pathname === '/licencas/permissoes') return 'politicas-acesso';
    if (pathname === '/licencas/gestao') return 'licencas';
    return 'dashboard';
};

export default function LicencasPage({openParent = true}) {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(openParent);
    const activeTab = getActiveTab(location.pathname);

    const menus = useMemo(() => ([
        {label: "Dashboard", id: "dashboard", selected: false, disabled: false, icon: <LayoutDashboard size={20} />, onClick: () => getActiveTab("/licencas/dashboard")},
        {label: "Licenças", id: "licencas", selected: true, disabled: false, icon: <ShieldCheck size={20} />, onClick: () => navigate("/licencas/gestao")},
    ]), [navigate]);

    return (
        <Box sx={{display: 'flex', minHeight: '100dvh'}}>
            <PageHeader
                title="Gestão de Licenças"
                titleAction={() => navigate("/")}
                itemsMenu={menus}
                onHandleMenuOpen={() => null}
                onHandleDrawerToggle={setOpen}
                openParent={open}
            />
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    boxSizing: 'border-box',
                    transition: theme.transitions.create(['margin', 'width']),
                    mt: 8,
                    backgroundColor: '#f9fafb',
                    minHeight: 'calc(100dvh - 64px)',
                }}
            >
                <Box sx={{bgcolor: '#ffffff', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 64, zIndex: 5}}>
                    <Box sx={{px: {xs: 2, md: 4}, py: 3}}>
                        <Typography variant="h4" sx={{fontWeight: 700, color: '#111827', mb: 0.5}}>
                            Gestão de Licenças
                        </Typography>
                        <Typography sx={{color: '#6b7280'}}>
                            Gerencie licenças ativas, monitore status e controle financeiro
                        </Typography>
                    </Box>
                    <Box sx={{px: {xs: 2, md: 4}}}>
                        <Box sx={{display: 'flex', gap: 0.5, borderBottom: '1px solid #e5e7eb', overflowX: 'auto'}}>
                            {tabs.map((tab) => {
                                const Icon = tab.icon;
                                const selected = activeTab === tab.id;

                                return (
                                    <ButtonBase
                                        key={tab.id}
                                        onClick={() => navigate(tab.path)}
                                        sx={{
                                            position: 'relative',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            px: 3,
                                            py: 1.75,
                                            color: selected ? '#0f766e' : '#6b7280',
                                            whiteSpace: 'nowrap',
                                            '&:hover': {
                                                color: '#111827',
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        <Icon size={20} />
                                        <Typography component="span" sx={{fontWeight: 500}}>
                                            {tab.label}
                                        </Typography>
                                        {selected ? <Box sx={{position: 'absolute', left: 0, right: 0, bottom: 0, height: 2, bgcolor: '#0f766e'}} /> : null}
                                    </ButtonBase>
                                );
                            })}
                        </Box>
                    </Box>
                </Box>

                <Box sx={{px: {xs: 2, md: 4}, py: 3}}>
                    {activeTab === 'dashboard' ? <LicencasDashboardPage /> : null}
                    {activeTab === 'licencas' ? <LicencasGestaoPage /> : null}
                    {activeTab === 'politicas-acesso' ? <PermissionsPage /> : null}
                </Box>
            </Box>
        </Box>
    );
}
